import { IconEdit } from '@tabler/icons-react';
import { isDefined } from '@zorro/common-utils';
import { Menu } from '@zorro/design-system/v2/Menu';
import { useAppRouter, useRoles } from '@zorro/web/ui-utils';

import { useAnalytics } from '../../../index';
import { EditEnrollmentTags } from '../../EnrollmentTags';
import {
  actionsMenuTooltipLabels,
  computeAreAllAorStatusesEligible,
  computeAreAllCarrierRequestStatusesEligible,
  getOnboardingPeriodsStatus,
} from '../ActionsMenuUtils';
import { ActionsMenuPrerequisitesTooltip } from '../ActionsRequirementTooltip';
import { ActionsSubMenu } from '../ActionsSubMenu';
import { ActionsMenuSection } from '../EmployeeActionsUtils';
import { EmployeeActionsOverlays } from '../employeeActions.consts';
import { BulkApplicationMenuItem } from './BulkApplicationMenuItem';

type EnrollmentActionsSectionProps = {
  recordsForActions: ActionsMenuSection['recordsForActions'];
  setOverlayId: ActionsMenuSection['setOverlayId'];
  closeMenu: () => void;
  isSingleEnrollmentPeriod: boolean;
  isSingleEmployer: boolean;
  isSingleEnrollmentTeam: boolean;
  isInEnrollmentsDatatable: boolean;
};

export const EnrollmentActionsSection = ({
  setOverlayId,
  closeMenu,
  recordsForActions,
  isSingleEmployer,
  isSingleEnrollmentTeam,
  isSingleEnrollmentPeriod,
  isInEnrollmentsDatatable,
}: EnrollmentActionsSectionProps) => {
  const { navigateToRoute } = useAppRouter();
  const { isZorroOperations } = useRoles();
  const { sendEvent } = useAnalytics();

  const isBulk = recordsForActions.length > 1;
  const {
    areAllOnboardingPeriodsExist,
    areAllOnboardingPeriodsSubmitted,
    areAllOnboardingPeriodsAtCarrierApplicationSentOrAfter,
    areAnyBenefitsSubmitted,
  } = getOnboardingPeriodsStatus(recordsForActions);

  const hasBenefitForAgent = isBulk
    ? recordsForActions.every((rec) => rec.benefitId)
    : recordsForActions.some((rec) => rec.benefitId);

  const canBulkAssignAcrossEmployers =
    isSingleEmployer || isSingleEnrollmentTeam;

  const areAllAorStatusesEligible =
    computeAreAllAorStatusesEligible(recordsForActions);
  const areAllCarrierRequestStatusesEligible =
    computeAreAllCarrierRequestStatusesEligible(recordsForActions);

  const hasBenefitForPaymentStatus = recordsForActions.some(
    (record) => record.benefitId
  );

  return (
    <ActionsSubMenu title="Enrollment actions" icon={IconEdit}>
      <ActionsMenuPrerequisitesTooltip
        title="Change election window"
        isAllEqual={
          isSingleEnrollmentPeriod &&
          areAllOnboardingPeriodsExist &&
          !areAnyBenefitsSubmitted
        }
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: isSingleEmployer,
            label: actionsMenuTooltipLabels.isSingleEmployer,
          },
          {
            isValid: !areAnyBenefitsSubmitted,
            label: actionsMenuTooltipLabels.areAnyBenefitsSubmitted,
          },
        ]}
      >
        <Menu.Item
          disabled={
            !isSingleEnrollmentPeriod ||
            !areAllOnboardingPeriodsExist ||
            areAnyBenefitsSubmitted
          }
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'enrollment_actions',
              action_name: 'CHANGE_ELECTION_WINDOW',
            });
            setOverlayId(EmployeeActionsOverlays.CHANGE_ELECTION_WINDOW);
          }}
        >
          {EmployeeActionsOverlays.CHANGE_ELECTION_WINDOW}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>

      {isZorroOperations && (
        <ActionsMenuPrerequisitesTooltip
          title="Reset election"
          isAllEqual={areAllOnboardingPeriodsExist && areAnyBenefitsSubmitted}
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          prerequisites={[
            {
              isValid: areAnyBenefitsSubmitted,
              label: actionsMenuTooltipLabels.areAllBenefitsSubmitted,
            },
          ]}
        >
          <Menu.Item
            disabled={!areAllOnboardingPeriodsExist || !areAnyBenefitsSubmitted}
            onClick={() => {
              void sendEvent('click_action', {
                action_category: 'enrollment_actions',
                action_name: 'RESET_ELECTION',
              });
              setOverlayId(EmployeeActionsOverlays.RESET_ELECTION);
            }}
          >
            {EmployeeActionsOverlays.RESET_ELECTION}
          </Menu.Item>
        </ActionsMenuPrerequisitesTooltip>
      )}

      <ActionsMenuPrerequisitesTooltip
        title="Waive coverage"
        isAllEqual={areAllOnboardingPeriodsExist && !areAnyBenefitsSubmitted}
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: !areAnyBenefitsSubmitted,
            label: actionsMenuTooltipLabels.areAnyBenefitsSubmitted,
          },
        ]}
      >
        <Menu.Item
          disabled={!areAllOnboardingPeriodsExist || areAnyBenefitsSubmitted}
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'enrollment_actions',
              action_name: 'WAIVE_COVERAGE',
            });
            setOverlayId(EmployeeActionsOverlays.WAIVE_COVERAGE);
          }}
        >
          {EmployeeActionsOverlays.WAIVE_COVERAGE}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>

      {isZorroOperations && (
        <ActionsMenuPrerequisitesTooltip
          title="Change shopping status"
          isAllEqual={areAllOnboardingPeriodsExist && !areAnyBenefitsSubmitted}
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          prerequisites={[
            {
              isValid: !areAnyBenefitsSubmitted,
              label: actionsMenuTooltipLabels.areAnyBenefitsSubmitted,
            },
          ]}
        >
          <Menu.Item
            disabled={!areAllOnboardingPeriodsExist || areAnyBenefitsSubmitted}
            onClick={() => {
              void sendEvent('click_action', {
                action_category: 'enrollment_actions',
                action_name: 'CHANGE_SHOPPING_STATUS',
              });
              setOverlayId(EmployeeActionsOverlays.CHANGE_SHOPPING_STATUS);
            }}
          >
            {EmployeeActionsOverlays.CHANGE_SHOPPING_STATUS}
          </Menu.Item>
        </ActionsMenuPrerequisitesTooltip>
      )}

      {isInEnrollmentsDatatable && (
        <Menu.Sub position="left-start">
          <Menu.Sub.Target>
            <Menu.Sub.Item>Edit tags</Menu.Sub.Item>
          </Menu.Sub.Target>
          <Menu.Sub.Dropdown>
            <EditEnrollmentTags
              records={recordsForActions
                .filter((record) => isDefined(record.onboardingPeriodId))
                .map((record) => ({
                  onboardingPeriodId: record.onboardingPeriodId!,
                  fullName: record.fullName,
                  enrollmentTags: record.enrollmentTags,
                }))}
              onSubmit={() => {
                void sendEvent('click_action', {
                  action_category: 'enrollment_actions',
                  action_name: 'EDIT_ENROLLMENT_TAGS',
                });
              }}
              onClose={closeMenu}
            />
          </Menu.Sub.Dropdown>
        </Menu.Sub>
      )}

      {isBulk && (
        <ActionsMenuPrerequisitesTooltip
          title="Flag as application sent"
          isAllEqual={areAllOnboardingPeriodsSubmitted}
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          prerequisites={[
            {
              isValid: areAllOnboardingPeriodsSubmitted,
              label: actionsMenuTooltipLabels.areAllOnboardingPeriodsSubmitted,
            },
          ]}
        >
          <Menu.Item
            disabled={!areAllOnboardingPeriodsSubmitted}
            onClick={() => {
              void sendEvent('click_action', {
                action_category: 'enrollment_actions',
                action_name: 'BULK_FLAG_AS_APPLICATION_SENT',
              });
              setOverlayId(
                EmployeeActionsOverlays.BULK_FLAG_AS_APPLICATION_SENT
              );
            }}
          >
            {EmployeeActionsOverlays.BULK_FLAG_AS_APPLICATION_SENT}
          </Menu.Item>
        </ActionsMenuPrerequisitesTooltip>
      )}

      {!isInEnrollmentsDatatable && !isBulk && (
        <Menu.Item
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'enrollment_actions',
              action_name: 'NAVIGATE_TO_EMPLOYEE_PAGE',
            });
            navigateToRoute('OPS_EMPLOYEE_PAGE', [
              {
                employerId: recordsForActions[0].employerId,
                employeeId: recordsForActions[0].employeeId,
              },
            ]);
          }}
        >
          Update enrollment status
        </Menu.Item>
      )}
      {!isInEnrollmentsDatatable && (
        <Menu.Item
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'enrollment_actions',
              action_name: 'ADD_ENROLLMENT_RECORD',
            });
            setOverlayId(EmployeeActionsOverlays.ADD_ENROLLMENT_RECORD);
          }}
        >
          {EmployeeActionsOverlays.ADD_ENROLLMENT_RECORD}
        </Menu.Item>
      )}

      {isInEnrollmentsDatatable && (
        <BulkApplicationMenuItem recordsForActions={recordsForActions} />
      )}

      <ActionsMenuPrerequisitesTooltip
        title="Assign enrollment"
        isAllEqual={
          (!isZorroOperations || canBulkAssignAcrossEmployers) &&
          areAllOnboardingPeriodsExist &&
          (isZorroOperations || hasBenefitForAgent)
        }
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: canBulkAssignAcrossEmployers,
            label: actionsMenuTooltipLabels.isSingleEmployerOrSharedTeam,
          },
          ...(!isZorroOperations
            ? [
                {
                  isValid: hasBenefitForAgent,
                  label: actionsMenuTooltipLabels.hasBenefit,
                },
              ]
            : []),
        ]}
      >
        <Menu.Item
          disabled={
            (isZorroOperations && !canBulkAssignAcrossEmployers) ||
            !areAllOnboardingPeriodsExist ||
            (!isZorroOperations && !hasBenefitForAgent)
          }
          onClick={async () => {
            await sendEvent('click_action', {
              action_category: 'enrollment_actions',
              action_name: 'ASSIGN_ENROLLMENT',
            });
            setOverlayId(EmployeeActionsOverlays.ASSIGN_ENROLLMENT);
          }}
        >
          {EmployeeActionsOverlays.ASSIGN_ENROLLMENT}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>

      {isInEnrollmentsDatatable && (
        <ActionsMenuPrerequisitesTooltip
          title="Change AOR cancellation status"
          isAllEqual={areAllAorStatusesEligible}
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          prerequisites={[
            {
              isValid: areAllAorStatusesEligible,
              label: actionsMenuTooltipLabels.areAllAorStatusesEligible,
            },
          ]}
        >
          <Menu.Item
            disabled={!areAllAorStatusesEligible}
            onClick={async () => {
              await sendEvent('click_action', {
                action_category: 'enrollment_actions',
                action_name: 'CHANGE_AOR_CANCELLATION_STATUS',
              });
              setOverlayId(
                EmployeeActionsOverlays.CHANGE_AOR_CANCELLATION_STATUS
              );
            }}
          >
            {EmployeeActionsOverlays.CHANGE_AOR_CANCELLATION_STATUS}
          </Menu.Item>
        </ActionsMenuPrerequisitesTooltip>
      )}

      {isInEnrollmentsDatatable && (
        <ActionsMenuPrerequisitesTooltip
          title="Change carrier request status"
          isAllEqual={areAllCarrierRequestStatusesEligible}
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          prerequisites={[
            {
              isValid: areAllCarrierRequestStatusesEligible,
              label:
                actionsMenuTooltipLabels.areAllCarrierRequestStatusesEligible,
            },
          ]}
        >
          <Menu.Item
            disabled={!areAllCarrierRequestStatusesEligible}
            onClick={async () => {
              await sendEvent('click_action', {
                action_category: 'enrollment_actions',
                action_name: 'CHANGE_CARRIER_REQUEST_STATUS',
              });
              setOverlayId(
                EmployeeActionsOverlays.CHANGE_CARRIER_REQUEST_STATUS
              );
            }}
          >
            {EmployeeActionsOverlays.CHANGE_CARRIER_REQUEST_STATUS}
          </Menu.Item>
        </ActionsMenuPrerequisitesTooltip>
      )}

      {isInEnrollmentsDatatable && !isBulk && (
        <ActionsMenuPrerequisitesTooltip
          title="Log enrollment call"
          isAllEqual={areAllOnboardingPeriodsExist}
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          prerequisites={[
            {
              isValid: areAllOnboardingPeriodsExist,
              label: actionsMenuTooltipLabels.areAllOnboardingPeriodsExist,
            },
          ]}
        >
          <Menu.Item
            disabled={!areAllOnboardingPeriodsExist}
            onClick={() => {
              void sendEvent('click_action', {
                action_category: 'enrollment_actions',
                action_name: 'LOG_ENROLLMENT_CALL',
              });
              setOverlayId(EmployeeActionsOverlays.LOG_ENROLLMENT_CALL);
            }}
          >
            {EmployeeActionsOverlays.LOG_ENROLLMENT_CALL}
          </Menu.Item>
        </ActionsMenuPrerequisitesTooltip>
      )}

      {!isBulk && (
        <ActionsMenuPrerequisitesTooltip
          title="Update payment status"
          isAllEqual={
            areAllOnboardingPeriodsAtCarrierApplicationSentOrAfter &&
            hasBenefitForPaymentStatus
          }
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          prerequisites={[
            {
              isValid: areAllOnboardingPeriodsAtCarrierApplicationSentOrAfter,
              label: actionsMenuTooltipLabels.isAtCarrierApplicationSentOrAfter,
            },
            {
              isValid: hasBenefitForPaymentStatus,
              label: actionsMenuTooltipLabels.hasBenefit,
            },
          ]}
        >
          <Menu.Item
            disabled={
              !areAllOnboardingPeriodsAtCarrierApplicationSentOrAfter ||
              !hasBenefitForPaymentStatus
            }
            onClick={() => {
              void sendEvent('click_action', {
                action_category: 'enrollment_actions',
                action_name: 'UPDATE_PAYMENT_STATUS',
              });
              setOverlayId(EmployeeActionsOverlays.UPDATE_PAYMENT_STATUS);
            }}
          >
            {EmployeeActionsOverlays.UPDATE_PAYMENT_STATUS}
          </Menu.Item>
        </ActionsMenuPrerequisitesTooltip>
      )}
    </ActionsSubMenu>
  );
};
